import { useState } from 'react';
import {
  ddToDmm,
  ddToDms,
  parseDmmToDd,
  parseDmsToDd,
} from '../../easymap/mapcontrols/LatLngConverter/latLongConversions';
import './CoordinateInput.scss';

// Decimal degrees rounded to 6 dp ≈ 0.1 m — beyond field-GPS precision, and
// avoids dumping raw floating-point noise (e.g. 60.00014485) into the field.
const DD_PRECISION = 6;

export type CoordinateFormat = 'DD' | 'DMM' | 'DMS';

export interface CoordinateInputProps {
  /** Latitude in decimal degrees, or null when unset. */
  lat: number | null;
  /** Longitude in decimal degrees, or null when unset. */
  lon: number | null;
  /** Fires with decimal-degree coordinates when both parse, or null otherwise. */
  onChange: (coords: { lat: number; lon: number } | null) => void;
  disabled?: boolean;
}

const FORMATS: { key: CoordinateFormat; label: string }[] = [
  { key: 'DD', label: 'D.D' },
  { key: 'DMM', label: 'DM.M' },
  { key: 'DMS', label: 'DMS.S' },
];

/** Render a decimal-degree value as a string in the given format (empty when unset). */
function formatValue(value: number | null, format: CoordinateFormat): string {
  if (value === null || !Number.isFinite(value)) return '';
  switch (format) {
    case 'DD':
      // Trim to sensible precision without trailing zeros (60.00014485 → 60.000145).
      return String(Number(value.toFixed(DD_PRECISION)));
    case 'DMM':
      return ddToDmm(value);
    case 'DMS':
      return ddToDms(value);
  }
}

/** Parse a field's text into decimal degrees for the given format (NaN when malformed). */
function parseValue(text: string, format: CoordinateFormat): number {
  switch (format) {
    case 'DD':
      return parseFloat(text);
    case 'DMM':
      return parseDmmToDd(text);
    case 'DMS':
      return parseDmsToDd(text);
  }
}

/**
 * Reusable three-format coordinate entry (#679). The canonical value is always
 * decimal degrees — the component parses whatever format the user selects and
 * emits decimal degrees to the parent, so callers never deal with DMM/DMS.
 */
export function CoordinateInput({ lat, lon, onChange, disabled }: CoordinateInputProps) {
  const [format, setFormat] = useState<CoordinateFormat>('DD');
  const [latText, setLatText] = useState<string>(() => formatValue(lat, 'DD'));
  const [lonText, setLonText] = useState<string>(() => formatValue(lon, 'DD'));
  const [error, setError] = useState<string | null>(null);

  const emit = (nextLat: string, nextLon: string, fmt: CoordinateFormat) => {
    const latDD = parseValue(nextLat, fmt);
    const lonDD = parseValue(nextLon, fmt);

    // Only fields the user has actually typed into can be "invalid".
    const latBad = nextLat.trim() !== '' && !Number.isFinite(latDD);
    const lonBad = nextLon.trim() !== '' && !Number.isFinite(lonDD);
    setError(latBad || lonBad ? 'Enter valid coordinates.' : null);

    if (Number.isFinite(latDD) && Number.isFinite(lonDD)) {
      onChange({ lat: latDD, lon: lonDD });
    } else {
      onChange(null);
    }
  };

  const handleLat = (value: string) => {
    setLatText(value);
    emit(value, lonText, format);
  };

  const handleLon = (value: string) => {
    setLonText(value);
    emit(latText, value, format);
  };

  const handleFormat = (next: CoordinateFormat) => {
    // Re-express the current values in the newly selected format.
    const latDD = parseValue(latText, format);
    const lonDD = parseValue(lonText, format);
    const nextLatText = Number.isFinite(latDD) ? formatValue(latDD, next) : latText;
    const nextLonText = Number.isFinite(lonDD) ? formatValue(lonDD, next) : lonText;
    setFormat(next);
    setLatText(nextLatText);
    setLonText(nextLonText);
    emit(nextLatText, nextLonText, next);
  };

  return (
    <div className="coordinate-input">
      <div className="coordinate-input__formats" role="group" aria-label="Coordinate format">
        {FORMATS.map(({ key, label }) => (
          <button
            key={key}
            type="button"
            aria-pressed={format === key}
            className={format === key ? 'is-active' : ''}
            disabled={disabled}
            onClick={() => handleFormat(key)}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="coordinate-input__fields">
        <div className="coordinate-input__field">
          <label htmlFor="coordinate-input-lat">Latitude</label>
          <input
            id="coordinate-input-lat"
            type="text"
            value={latText}
            disabled={disabled}
            onChange={(e) => handleLat(e.target.value)}
          />
        </div>

        <div className="coordinate-input__field">
          <label htmlFor="coordinate-input-lon">Longitude</label>
          <input
            id="coordinate-input-lon"
            type="text"
            value={lonText}
            disabled={disabled}
            onChange={(e) => handleLon(e.target.value)}
          />
        </div>
      </div>

      {error && (
        <p className="coordinate-input__error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

export default CoordinateInput;
